import emailjs from '@emailjs/browser';

// EmailJS configuration
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID_TICKET = import.meta.env.VITE_EMAILJS_TEMPLATE_ID_TICKET;
const EMAILJS_TEMPLATE_ID_RECEIPT = import.meta.env.VITE_EMAILJS_TEMPLATE_ID_RECEIPT;
const EMAILJS_TEMPLATE_ID_RESET = import.meta.env.VITE_EMAILJS_TEMPLATE_ID_RESET;
const SUPPORT_EMAIL = 'support@travistechrepair.com';

interface TicketEmailData {
  customerName: string;
  customerEmail: string;
  ticketId: string;
  category: string;
  description: string;
  date: string;
  phone?: string;
  preferredDate?: string;
  preferredTime?: string;
  make?: string;
  model?: string;
  serialNumber?: string;
  price?: number;
}

interface ReceiptEmailData {
  customerName: string;
  customerEmail: string;
  ticketId: string;
  serviceDescription: string;
  amount: number;
  date: string;
  paymentMethod: string;
  technician?: string;
  warrantyPeriod?: string;
}

export const sendTicketEmail = async (data: TicketEmailData): Promise<boolean> => {
  try {
    emailjs.init(EMAILJS_PUBLIC_KEY);

    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID_TICKET,
      {
        to_email: data.customerEmail,
        to_name: data.customerName,
        ticket_id: data.ticketId,
        category: data.category,
        description: data.description,
        date: new Date(data.date).toLocaleDateString(),
        phone: data.phone || 'Not provided',
        preferred_date: data.preferredDate || 'Not specified',
        preferred_time: data.preferredTime || 'Not specified',
        make: data.make || 'Not specified',
        model: data.model || 'Not specified',
        serial_number: data.serialNumber || 'Not provided',
        price: data.price ? `$${data.price.toFixed(2)}` : 'To be determined',
        from_name: 'Travis Tech Support',
        reply_to: SUPPORT_EMAIL
      }
    );

    return true;
  } catch (error) {
    console.error('Failed to send ticket email:', error);
    throw error;
  }
};

export const sendReceiptEmail = async (data: ReceiptEmailData): Promise<boolean> => {
  try {
    emailjs.init(EMAILJS_PUBLIC_KEY);

    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID_RECEIPT,
      {
        to_email: data.customerEmail,
        to_name: data.customerName,
        ticket_id: data.ticketId,
        service_description: data.serviceDescription,
        amount: `$${data.amount.toFixed(2)}`,
        date: new Date(data.date).toLocaleDateString(),
        payment_method: data.paymentMethod,
        technician: data.technician || 'Travis Tech Support',
        warranty_period: data.warrantyPeriod || '30 days',
        company_name: 'Travis Tech LLC',
        company_address: '18117 Biscayne Blvd Suite #4281, Miami, FL 33160',
        company_phone: '850-273-8264',
        company_email: SUPPORT_EMAIL,
        from_name: 'Travis Tech Support',
        reply_to: SUPPORT_EMAIL
      }
    );

    return true;
  } catch (error) {
    console.error('Failed to send receipt email:', error);
    throw error;
  }
};

export const sendPasswordResetEmail = async (email: string, resetToken: string): Promise<boolean> => {
  try {
    emailjs.init(EMAILJS_PUBLIC_KEY);

    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID_RESET,
      {
        to_email: email,
        to_name: email,
        reset_link: `https://travistechrepair.netlify.app/password-reset?token=${resetToken}`,
        company_name: 'Travis Tech LLC',
        support_email: SUPPORT_EMAIL,
        support_phone: '850-273-8264',
        from_name: 'Travis Tech Support',
        reply_to: SUPPORT_EMAIL
      }
    );

    return true;
  } catch (error) {
    console.error('Failed to send password reset email:', error);
    throw error;
  }
};